import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, TOKEN_KEY, apiErrorMessage } from './apiClient';

const MODE_KEY = 'radius_org_mode';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgMode, setOrgModeState] = useState(false);

  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) { setUser(null); return null; }
      const res = await api.get('/auth/me');
      setUser(res.data);
      return res.data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshUser();
      const savedMode = localStorage.getItem(MODE_KEY);
      setOrgModeState(savedMode === '1');
      setLoading(false);
    })();
  }, [refreshUser]);

  const setOrgMode = async (v) => {
    setOrgModeState(v);
    localStorage.setItem(MODE_KEY, v ? '1' : '0');
  };

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      setUser(res.data.user);
      if (res.data.user?.role === 'organizer') {
        await setOrgMode(true);
      } else {
        await setOrgMode(false);
      }
    } catch (e) {
      throw new Error(apiErrorMessage(e));
    }
  };

  const register = async (email, password, full_name, date_of_birth) => {
    try {
      const res = await api.post('/auth/register', { email, password, full_name, date_of_birth });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      setUser(res.data.user);
      await setOrgMode(false);
    } catch (e) {
      throw new Error(apiErrorMessage(e));
    }
  };

  const completeProfile = async (date_of_birth) => {
    try {
      const res = await api.post('/auth/complete-profile', { date_of_birth });
      setUser(res.data);
    } catch (e) {
      throw new Error(apiErrorMessage(e));
    }
  };

  const logout = async () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(MODE_KEY);
    setOrgModeState(false);
    setUser(null);
  };

  const isOrganizer = user?.role === 'organizer';
  const effectiveOrgMode = isOrganizer && orgMode;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthed: !!user,
        isOrganizer,
        orgMode: effectiveOrgMode,
        setOrgMode,
        login,
        register,
        completeProfile,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
